import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, mrpTotal, count } = useCart();
  const savings = mrpTotal - subtotal;

  if (items.length === 0) {
    return (
      <main className="max-w-[800px] mx-auto px-4 py-20 text-center" data-testid="cart-empty">
        <div className="w-20 h-20 rounded-full bg-neutral-100 mx-auto flex items-center justify-center">
          <ShoppingBag className="w-8 h-8 text-neutral-400" />
        </div>
        <h1 className="mt-6 font-display text-3xl md:text-4xl font-black">Your cart is empty</h1>
        <p className="mt-2 text-neutral-500">Add some essentials to get started.</p>
        <Link
          to="/shop"
          className="mt-8 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-neutral-900 hover:bg-[#FF5A1F] text-white font-semibold text-sm transition"
          data-testid="empty-cart-shop-btn"
        >
          Continue Shopping <ArrowRight className="w-4 h-4" />
        </Link>
      </main>
    );
  }

  return (
    <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-14" data-testid="cart-page">
      <h1 className="font-display text-3xl md:text-5xl font-black leading-tight">Your Bag</h1>
      <p className="text-neutral-500 mt-1 text-sm md:text-base">{count} item{count !== 1 ? "s" : ""}</p>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6 lg:gap-10 mt-6 md:mt-10">
        <div className="space-y-3 md:space-y-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex gap-3 md:gap-6 bg-white border border-neutral-200 rounded-2xl p-3 md:p-4"
              data-testid={`cart-item-${item.id}`}
            >
              <Link to={`/product/${item.slug}`} className="w-20 h-20 md:w-32 md:h-32 bg-neutral-100 rounded-xl overflow-hidden flex-shrink-0">
                <img src={item.image} alt={item.title} className="w-full h-full object-contain p-2 md:p-3" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${item.slug}`} className="font-semibold text-sm md:text-base text-neutral-900 line-clamp-2 hover:text-[#FF5A1F]">
                  {item.title}
                </Link>
                <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                  <span className="font-bold text-sm md:text-base">₹{item.sale}</span>
                  <span className="text-xs text-neutral-400 line-through">₹{item.mrp}</span>
                  <span className="md:hidden ml-auto font-bold text-base">₹{item.sale * item.qty}</span>
                </div>
                <div className="mt-3 flex items-center gap-3 md:gap-4 flex-wrap">
                  <div className="flex items-center border border-neutral-300 rounded-full h-9">
                    <button onClick={() => updateQty(item.id, item.qty - 1)} className="w-9 h-9 flex items-center justify-center" data-testid={`dec-${item.id}`}><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-7 text-center text-sm font-semibold">{item.qty}</span>
                    <button onClick={() => updateQty(item.id, item.qty + 1)} className="w-9 h-9 flex items-center justify-center" data-testid={`inc-${item.id}`}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-xs md:text-sm text-neutral-500 hover:text-[#FF5A1F] flex items-center gap-1.5"
                    data-testid={`remove-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                </div>
              </div>
              <div className="text-right hidden md:block">
                <div className="font-bold text-lg">₹{item.sale * item.qty}</div>
              </div>
            </div>
          ))}
        </div>

        <aside className="bg-neutral-50 rounded-3xl p-5 md:p-8 h-fit lg:sticky lg:top-28" data-testid="order-summary">
          <h3 className="font-display text-xl font-black">Order Summary</h3>
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex justify-between text-neutral-600"><span>Subtotal</span><span>₹{mrpTotal}</span></div>
            <div className="flex justify-between text-[#FF5A1F]"><span>Discount</span><span>– ₹{savings}</span></div>
            <div className="flex justify-between text-neutral-600"><span>Shipping</span><span className="text-green-600 font-semibold">FREE</span></div>
            <div className="border-t border-neutral-300 pt-4 flex justify-between items-baseline">
              <span className="font-bold">Total</span>
              <span className="font-display text-2xl font-black" data-testid="cart-total">₹{subtotal}</span>
            </div>
          </div>
          <Link
            to="/checkout"
            className="mt-6 w-full h-12 rounded-full bg-[#FF5A1F] hover:bg-[#e64a0f] text-white font-bold text-sm flex items-center justify-center gap-2 transition"
            data-testid="checkout-btn"
          >
            Proceed to Checkout <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/shop"
            className="mt-3 w-full h-11 rounded-full border border-neutral-300 font-semibold text-sm flex items-center justify-center hover:bg-white transition"
          >
            Continue Shopping
          </Link>
          <p className="mt-4 text-xs text-neutral-500 text-center">
            Secure checkout · Free returns within 10 days
          </p>
        </aside>
      </div>
    </main>
  );
}
